import React, { useState } from 'react';
import {
  Alert,
  Card,
  Divider,
  Space,
  Tag,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  ControlOutlined,
  CarOutlined,
  CloudSyncOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  InboxOutlined,
  ImportOutlined,
  EditOutlined,
  FileTextOutlined,
  HomeOutlined,
  PieChartOutlined,
  BoxPlotOutlined,
  OrderedListOutlined,
  ProjectOutlined,
  SearchOutlined,
  SettingOutlined,
  StockOutlined,
  SwapOutlined,
  TableOutlined,
  CheckSquareOutlined,
} from '@ant-design/icons';
import './HelpPage.css';

const { Title, Text, Paragraph } = Typography;
const shortcutRows = [
  { key: '/', action: 'Focus order search', detail: 'Start searching without reaching for the mouse.' },
  { key: 'V', action: 'View selected order', detail: 'Opens details when exactly one order is selected.' },
  { key: 'E', action: 'Edit selected order', detail: 'Opens the edit panel when exactly one order is selected.' },
  { key: 'R', action: 'Refresh orders', detail: 'Reloads the latest order data.' },
  { key: 'Esc', action: 'Close active panel', detail: 'Closes the open drawer or modal.' },
];

const workflowSteps = [
  {
    icon: <SearchOutlined />,
    title: 'Find the work',
    description: 'Use the Orders search for Order ID, customer, phone, AWB, or courier. Use filters when you need a focused queue.',
  },
  {
    icon: <FilterOutlined />,
    title: 'Narrow the queue',
    description: 'Combine date, AWB, packing, shipment, hold, stock issue, courier, and product filters. Active filters appear below the filter bar.',
  },
  {
    icon: <TableOutlined />,
    title: 'Review an order',
    description: 'Double-click any order row, use the View action, or select one row and press V to open Order Details.',
  },
  {
    icon: <CheckCircleOutlined />,
    title: 'Complete the action',
    description: 'Edit an eligible order, update AWB details, resync from Odoo, or select multiple orders for bulk actions.',
  },
];

const actionRows = [
  ['View', 'Opens the Order Details drawer with customer, shipment, warehouse, packing, products, and activity information.'],
  ['Edit', 'Updates editable order fields. Completed orders cannot be edited.'],
  ['Re-sync', 'Fetches the selected eligible order from Odoo. Completed and cancelled orders do not show this action.'],
  ['Copy', 'Copies the Order ID or AWB from the table.'],
  ['Columns', 'Controls which table columns are visible.'],
  ['Views', 'Saves and applies frequently used filter arrangements.'],
];

const pageGuideItems = [
  { key: 'dashboard', label: 'Dashboard', icon: <HomeOutlined /> },
  { key: 'orders', label: 'Orders', icon: <OrderedListOutlined />, available: true },
  { key: 'sync', label: 'Orders Sync', icon: <CloudSyncOutlined /> },
  { key: 'packing', label: 'Packing', icon: <BoxPlotOutlined /> },
  { key: 'packing-material', label: 'Packing Material', icon: <ProjectOutlined /> },
  { key: 'master-packing-sheet', label: 'Master Packing Sheet', icon: <FileTextOutlined /> },
  { key: 'picking', label: 'Picking', icon: <CheckSquareOutlined /> },
  { key: 'delivery', label: 'Delivery', icon: <CarOutlined /> },
  { key: 'products', label: 'Products', icon: <EditOutlined /> },
  { key: 'stock-assessment', label: 'Stock Assessment', icon: <StockOutlined /> },
  { key: 'rto-inbound', label: 'RTO Inbound', icon: <ImportOutlined /> },
  { key: 'rto-reporting', label: 'RTO Reporting', icon: <PieChartOutlined /> },
  { key: 'rto-inventory', label: 'RTO Inventory', icon: <InboxOutlined /> },
  { key: 'replacements', label: 'Replacements', icon: <SwapOutlined /> },
  { key: 'settings', label: 'Settings', icon: <SettingOutlined /> },
];

const HelpPage = ({ darkTheme }) => {
  const [selectedPage, setSelectedPage] = useState('orders');
  const selectedPageMeta = pageGuideItems.find((page) => page.key === selectedPage) || pageGuideItems[1];

  return (
  <div className={`help-page${darkTheme ? ' help-page-dark' : ''}`}>
    <aside className="help-page-sidebar">
      <div className="help-sidebar-brand">
        <span className="help-sidebar-brand-icon"><ControlOutlined /></span>
        <div>
          <Text className="help-sidebar-eyebrow">PACKING CONSOLE</Text>
          <Text strong>Help center</Text>
        </div>
      </div>
      <div className="help-sidebar-intro">
        <Text strong>Page guides</Text>
        <Text type="secondary">Choose a page to see its workflow and shortcuts.</Text>
      </div>
      <nav className="help-page-nav" aria-label="Help pages">
        {pageGuideItems.map((page) => (
          <button
            className={`help-page-nav-item${selectedPage === page.key ? ' is-active' : ''}`}
            type="button"
            key={page.key}
            onClick={() => setSelectedPage(page.key)}
            aria-current={selectedPage === page.key ? 'page' : undefined}
          >
            <span className="help-page-nav-icon">{page.icon}</span>
            <span className="help-page-nav-label">{page.label}</span>
            {page.available ? <span className="help-page-nav-status">Live</span> : <span className="help-page-nav-status is-soon">Soon</span>}
          </button>
        ))}
      </nav>
    </aside>

    <main className="help-page-main">
      <header className="help-page-hero">
        <div>
          <Text className="help-page-eyebrow">WORKSPACE GUIDE / {selectedPageMeta.label.toUpperCase()}</Text>
          <Title level={2}>Help &amp; shortcuts</Title>
          <Paragraph>
            Practical guidance for moving through the Packing Console quickly and consistently.
          </Paragraph>
        </div>
        <div className="help-page-hero-icon" aria-hidden="true">
          <ControlOutlined />
        </div>
      </header>

      {selectedPage === 'orders' ? (
      <div className="help-page-content">
      <Card className="help-page-section-card" bordered={false}>
        <div className="help-section-heading">
          <Space align="center">
            <span className="help-section-icon"><OrderedListOutlined /></span>
            <div>
              <Text className="help-section-kicker">PAGE GUIDE</Text>
              <Title level={3}>Orders</Title>
            </div>
          </Space>
          <Tag color="success">Available</Tag>
        </div>

        <Alert
          className="help-page-alert"
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          message="Orders is the work queue for finding, reviewing, editing, and resyncing orders."
        />

        <Divider orientation="left">How to use Orders</Divider>
        <div className="help-workflow-grid">
          {workflowSteps.map((step) => (
            <div className="help-workflow-item" key={step.title}>
              <span className="help-workflow-icon">{step.icon}</span>
              <div>
                <Text strong>{step.title}</Text>
                <Paragraph>{step.description}</Paragraph>
              </div>
            </div>
          ))}
        </div>

        <Divider orientation="left">Keyboard shortcuts</Divider>
        <div className="help-shortcut-list">
          {shortcutRows.map((shortcut) => (
            <div className="help-shortcut-row" key={shortcut.key}>
              <kbd>{shortcut.key}</kbd>
              <div>
                <Text strong>{shortcut.action}</Text>
                <Text type="secondary">{shortcut.detail}</Text>
              </div>
            </div>
          ))}
        </div>
        <Text className="help-note" type="secondary">
          Shortcuts are ignored while typing in search, filters, inline fields, dialogs, or other interactive controls.
        </Text>

        <Divider orientation="left">Table actions</Divider>
        <div className="help-action-list">
          {actionRows.map(([action, description]) => (
            <div className="help-action-row" key={action}>
              <Tag>{action}</Tag>
              <Text>{description}</Text>
            </div>
          ))}
        </div>

        <Divider orientation="left">Recommended daily flow</Divider>
        <ol className="help-ordered-list">
          <li>Start with the missing AWB and pending order KPI cards.</li>
          <li>Use filters to create a focused work queue.</li>
          <li>Review customer, courier, AWB, warehouse, and product information before packing.</li>
          <li>Use bulk actions only after confirming the selected order count.</li>
          <li>Refresh the page after external changes or Odoo resync operations.</li>
        </ol>
      </Card>

      <Card className="help-page-next-card" bordered={false}>
        <Space align="start">
          <SettingOutlined className="help-next-icon" />
          <div>
            <Text strong>More page guides are planned</Text>
            <Paragraph>
              Packing, Picking, Delivery, Orders Sync, and RTO workflows can use the same format for shortcuts, step-by-step usage, and operational notes.
            </Paragraph>
          </div>
        </Space>
      </Card>
      </div>
      ) : (
        <Card className="help-coming-soon-card" bordered={false}>
          <span className="help-coming-soon-icon">{selectedPageMeta.icon}</span>
          <Text className="help-section-kicker">PAGE GUIDE</Text>
          <Title level={3}>{selectedPageMeta.label}</Title>
          <Paragraph>
            The {selectedPageMeta.label} workflow guide is being prepared. This page is already listed here so the Help center can become the single reference point for the whole application.
          </Paragraph>
          <Tag color="gold">Guide coming soon</Tag>
        </Card>
      )}
    </main>
  </div>
  );
};

export default HelpPage;
